import React, { useEffect, useState } from "react";
import { TiVendorMicrosoft } from "react-icons/ti";
import { FiMail, FiLock, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { AiOutlineLock } from "react-icons/ai";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";

import { myLocalStorage } from "../../../components/StorageHelper";
import useUserStore from "../../../services/userStore";
import { getClientId } from "../../../services/Auth";
import { registerSessionTokens } from "../../../utils/sessionTokenManager";
import {
  decodeKeycloakToken,
  sendTokenToBackend,
  storeLatestTenant,
} from "../../../utils/tokenHelpers";
import { setInitialSessionTime } from "../../../services/Helpers";
import useAuth from "../../../hooks/useAuth";
import { NGROK } from "../../../APIs";
import UserService from "../../../services/UserService";
import AuthenticationPage from "./components/AuthenticationPage";
import { useNavigate } from "react-router-dom";


// Step indicator component
const StepIndicator = ({ currentStep }) => {
  return (
    <div className="flex justify-center gap-2 mb-6">
      {[1, 2].map((step) => (
        <div
          key={step}
          className={`h-2 rounded-full transition-all duration-500 ${currentStep === step ? "w-8 bg-indigo-600" : "w-2 bg-gray-300"
            }`}
        />
      ))}
    </div>
  );
};

const AuthSignIn = () => {
  const { signIn } = useAuth();
  const { setAzureRedirectURI } = useUserStore((state) => state);
  const fetchUserData = useUserStore((state) => state.getUserData);
  const navigate = useNavigate();
  // Form states
  const [step, setStep] = useState(1); // 1 = email, 2 = password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [realmName, setRealmName] = useState("");
  const [redirectURI, setRedirectURI] = useState("");
  const [ssoConfig, setSsoConfig] = useState(null);
  const [showAzureSSO, setShowAzureSSO] = useState(false);

  // UI states
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [globalLoading, setGlobalLoading] = useState(false);

  // Validation schema
  const emailSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .trim("No whitespace allowed")
      .strict(true)
      .required("Email is required"),
  });

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: emailSchema,
    onSubmit: () => { },
  });

  // API calls
  const getTenantsInfo = async () => {
    try {
      const axiosInstance = axios.create();
      return await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.trim().toLowerCase()}`
      );
    } catch (error) {
      setTenantName("");
      console.error(error);
    }
  };

  const checkIfUserExist = async (email) => {
    const tenants = await getTenantsInfo(email.toLowerCase().trim());

    if (!tenants || !tenants.data?.length) {
      setTenantName("");
      setSubmitLoading(false);
      return false;
    } else if (tenants?.data?.length) {
      setTenantName(tenants?.data[0].tenantName);
      myLocalStorage.setItem("role", tenants?.data[0].role);
      setSubmitLoading(false);
      return tenants?.data[0];
    }
  };

  const keycloakInitAndLogin = async (realm, email) => {
    try {
      setGlobalLoading(true);
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("realm", realm);
      // Clear lastVisitedPage to ensure redirect to endpoints after login
      myLocalStorage.removeItem("lastVisitedPage");

      await UserService.initKeycloak(realm);
      await UserService.doLogin(email.toLowerCase().trim());
    } catch (error) {
      setGlobalLoading(false);
      console.log(error);
    }
  };

  const findTheSSOConfig = async (currentTenantName) => {


    try {
      const res = await axios.get(`${NGROK}/api/computers/sso-check`, {
        params: { tenantName: currentTenantName },
      });

      if (res.data) {
        myLocalStorage.setItem("ssoConfig", JSON.stringify(res.data));
        setSsoConfig(res?.data?.settingsValue);
      }
    } catch (error) {
      setSsoConfig(null);
      myLocalStorage.removeItem("ssoConfig");
    }
  };
  useEffect(() => {
    findTheSSOConfig("");
  }, []);

  useEffect(() => {
    const resetLoadingState = () => {
      setGlobalLoading(false);
      setSubmitLoading(false);
    };

    window.addEventListener("pageshow", resetLoadingState);

    return () => {
      window.removeEventListener("pageshow", resetLoadingState);
    };
  }, []);

  // Event handlers
  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val.toLowerCase().trim());
    setSsoConfig(null);
    myLocalStorage.removeItem("ssoConfig");
    formik.handleChange(e);
    setSubmitLoading(false);
    setIsSubmitted(false);
    setEmailErrorMessage("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setIsSubmitted(false);
    setPasswordError("");
  };

  const handleBackToEmail = () => {
    setStep(1);
    setPassword("");
    setPasswordError("");
    setIsSubmitted(false);
    setShowAzureSSO(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setIsSubmitted(true);
    formik.handleSubmit();
    setEmailErrorMessage("");

    if (!formik.errors.email && email) {
      const user = await checkIfUserExist(email.toLowerCase().trim());
      if (!user) {
        setEmailErrorMessage("This user doesn't exist.");
        setSubmitLoading(false);
        return;
      }

      // Handle SYS_ADMIN
      if (user.role === "SYS_ADMIN") {
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        setTenantName(user.realmName);
        setRealmName(user.legacyRealm.realmName.toLowerCase());
        setStep(2);
        setSubmitLoading(false);
        return;
      }

      // Handle Azure SSO
      if (user.isFromSSO === "azureSSO") {
        setRedirectURI(user.redirectURL);
        setShowAzureSSO(true);
        setSubmitLoading(false);
        return;
      }

      // Handle Keycloak
      if (user.realm?.keycloakAccountExists) {
        const realm =
          user.role === "TENANT_USER"
            ? user.realm.realmName
            : "whiteswan_tenants";
        keycloakInitAndLogin(realm, email.toLowerCase().trim());
      } else if (user.legacyRealm?.keycloakAccountExists) {
        const realm =
          user.role === "TENANT_USER"
            ? user.legacyRealm.realmName
            : "whiteswan_tenants_legacy";
        keycloakInitAndLogin(realm, email.toLowerCase().trim());
      } else {
        setEmailErrorMessage("This user doesn't exist.");
        setSubmitLoading(false);
      }
    } else {
      setSubmitLoading(false);
    }

    setIsSubmitted(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setSubmitLoading(true);
    const clientId = getClientId(realmName);

    try {
      const loginData = {
        username: email,
        password,
        realmName,
        clientId,
      };

      const { access_token, refresh_token, userInfo } = await signIn(loginData);

      registerSessionTokens(access_token, refresh_token);
      myLocalStorage.setItem("email", email);
      myLocalStorage.setItem("realm", realmName);
      if (userInfo?.role) {
        myLocalStorage.setItem("role", userInfo.role);
      }
      // Clear lastVisitedPage to ensure redirect to endpoints after login
      myLocalStorage.removeItem("lastVisitedPage");

      const { exp } = decodeKeycloakToken(access_token);
      await sendTokenToBackend(realmName, email, userInfo.role, access_token, exp);
      setInitialSessionTime(30);
      await storeLatestTenant();
      await fetchUserData(email, { force: true });
      navigate("/endpoints");
    } catch (error) {
      console.error(error);
      setPasswordError("Wrong password");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAzureSSO = async () => {
    myLocalStorage.setItem("email", email.toLowerCase().trim());
    myLocalStorage.setItem("realm", "whiteswan_tenants_legacy");
    // Clear lastVisitedPage to ensure redirect to endpoints after login
    myLocalStorage.removeItem("lastVisitedPage");
    setAzureRedirectURI(redirectURI);
    window.location.href = redirectURI;
  };

  const handleSSOSignIn = async () => {
    if (ssoConfig) {
      let realmName = ssoConfig;
      let clientId = `otp-auth-${realmName}`;

      myLocalStorage.setItem("email", "");
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("realm", realmName);
      myLocalStorage.setItem("clientId", clientId);
      keycloakInitAndLogin(realmName, email.toLowerCase().trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (step === 1) {
        handleEmailSubmit(e);
      } else {
        handlePasswordSubmit(e);
      }
    }
  };

  // Global loading overlay
  if (globalLoading) {
    return (
      <AuthenticationPage>
        <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          </div>
        </div>
      </AuthenticationPage>
    );
  }

  return (
    <AuthenticationPage>
      <div className="w-full max-w-md px-4">
        {/* Sign In Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
          {/* Top Gradient Border */}
          <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>

          <div className="p-6 flex flex-col min-h-[500px]">
            {/* Header */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg mb-3">
                <AiOutlineLock className="text-white text-2xl" />
              </div>
              <h1 className="text-xl font-bold">
                Welcome Back
              </h1>
              <p className="text-xs text-gray-600">
                {step === 1
                  ? "Enter your email to continue"
                  : "Enter your password"}
              </p>
            </div>

            {/* Step Indicator */}
            {!showAzureSSO && <StepIndicator currentStep={step} />}

            {/* Main Content Area with fixed height */}
            <div className="flex-1 flex flex-col gap-4">
              {/* Combined Email/Password Form (reuses same space) */}
              {!showAzureSSO && (
                <div className="flex-1 flex flex-col gap-4 min-h-[220px]">
                  {/* Email Field - Always visible */}
                  <form onSubmit={handleEmailSubmit}>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiMail className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={email}
                          onChange={handleEmailChange}
                          onKeyDown={handleKeyDown}
                          disabled={submitLoading || step === 2}
                          placeholder="you@example.com"
                          className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                          autoFocus={step === 1}
                        />
                        {step === 1 && (
                          <button
                            type="submit"
                            disabled={submitLoading || !email}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <div className="bg-indigo-100 hover:bg-indigo-200 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                              {submitLoading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                              ) : (
                                <FiArrowRight className="text-indigo-600 text-lg" />
                              )}
                            </div>
                          </button>
                        )}
                      </div>
                    </div>

                    {step === 1 && formik.errors.email && isSubmitted && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mt-3 animate-slideDown">
                        {formik.errors.email}
                      </div>
                    )}

                    {step === 1 && emailErrorMessage && isSubmitted && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mt-3 animate-slideDown">
                        {emailErrorMessage}
                      </div>
                    )}
                  </form>

                  {/* Password Field replaces space below email */}
                  {step === 2 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={handleBackToEmail}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          <FiArrowLeft className="text-gray-600" />
                        </button>
                        <span className="text-xs text-gray-600 font-medium">
                          Change email
                        </span>
                      </div>
                      <form onSubmit={handlePasswordSubmit}>
                        <div>
                          <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Password
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FiLock className="text-gray-400" />
                            </div>
                            <input
                              type="password"
                              id="password"
                              name="password"
                              value={password}
                              onChange={handlePasswordChange}
                              onKeyDown={handleKeyDown}
                              disabled={submitLoading}
                              placeholder="Enter your password"
                              className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                              autoFocus
                            />
                            <button
                              type="submit"
                              disabled={submitLoading || !password}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                              <div className="bg-indigo-100 hover:bg-indigo-200 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                {submitLoading ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                                ) : (
                                  <FiArrowRight className="text-indigo-600 text-lg" />
                                )}
                              </div>
                            </button>
                          </div>
                        </div>
                        {passwordError && isSubmitted && (
                          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mt-3 animate-slideDown">
                            {passwordError}
                          </div>
                        )}
                      </form>
                    </div>
                  )}
                </div>
              )}

              {/* Azure SSO dedicated space when triggered */}
              {showAzureSSO && (
                <div className="flex-1 flex flex-col gap-3 min-h-[220px]">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={handleBackToEmail}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
                    >
                      <FiArrowLeft className="text-gray-600" />
                    </button>
                    <span className="text-xs text-gray-600 font-medium">
                      {email}
                    </span>
                  </div>

                  <button
                    onClick={handleAzureSSO}
                    className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2.5 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  >
                    <TiVendorMicrosoft size={24} />
                    Sign in with Microsoft
                  </button>
                </div>
              )}
            </div>

            {/* Footer links and SSO anchored at bottom */}
            <div className="mt-6 space-y-3">
              <div className="text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate("/signUp")}
                  className="font-bold text-indigo-600 hover:text-indigo-700 hover:underline transition-all bg-transparent border-none p-0 cursor-pointer"
                >
                  Sign up!
                </button>
              </div>
              <div className="text-center text-sm">
                <a
                  href="/forgot-password"
                  className="font-semibold text-indigo-600 hover:text-indigo-700 hover:underline transition-all"
                >
                  Forgot password?
                </a>
              </div>

              {!showAzureSSO && ssoConfig && (
                <div className="space-y-3 pt-1">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <span className="text-sm font-semibold text-gray-500">
                      OR
                    </span>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>

                  <button
                    onClick={handleSSOSignIn}
                    className="w-full border-2 border-indigo-300 text-indigo-600 py-2.5 px-6 rounded-xl font-semibold hover:bg-indigo-50 hover:border-indigo-600 hover:-translate-y-0.5 hover:shadow-md transition-all"
                  >
                    Sign in with SSO
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default AuthSignIn;
